import { useCallback, memo, useState, useRef, useEffect, KeyboardEvent, FormEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons/faEdit';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons/faCircleExclamation';
import { useHasPermission } from '@hooks/has-permission/useHasPermission';
import { exist } from 'components/global/utils';



interface EditableGridCellProps {
	externalValue?: string | null,
	className?: string | null,
	isApiError?: boolean,
	onPressSave?: (value: string) => void,
	requiredRoles?: string | string[],
}


const EditableGridCell = (props: EditableGridCellProps) => {
	const { externalValue, className, isApiError, onPressSave, requiredRoles } = props;

	const hasPermission = useHasPermission();
	const permissionToEdit = requiredRoles ? hasPermission(requiredRoles) : true;
	const editableRef = useRef<HTMLDivElement>(null);
	const [ activeEdit, setActiveEdit ] = useState(false);
	const [ value, setValue ] = useState(externalValue ? externalValue : '');
	const [ tempValue, setTempValue ] = useState(value);

	
	useEffect(() => {
		if (externalValue !== null && externalValue !== undefined) {
			setTempValue(externalValue);
		}
	}, [ externalValue ]);


	// set caret always on end of text
	useEffect(() => {
		let isTargetFocused = document.activeElement === editableRef.current;
		let selection = window.getSelection();
		
		if (!editableRef || !editableRef.current || !isTargetFocused || !selection || tempValue.length === 0)
			return;

		let range = document.createRange();
		range.setStart(editableRef.current, 1);
		range.collapse(true);
		selection.removeAllRanges();
		selection.addRange(range);
	}, [ tempValue ]);


	// set focus and caret on end when component becomes editable
	useEffect(() => {
		if (activeEdit && editableRef && editableRef.current) {
			editableRef.current.focus();

			let selection = window.getSelection();
			if (tempValue.length === 0 || !selection) return;

			let range = document.createRange();
			range.setStart(editableRef.current, 1);
			range.collapse(true);
			selection.removeAllRanges();
			selection.addRange(range);
		}
	}, [ activeEdit ]);


	const onChangeInput = useCallback((event: FormEvent<HTMLParagraphElement>) => {
		if (event && event.currentTarget) {
			setTempValue(event.currentTarget.innerText.replace(/\r?\n|\r/g, ""));
		}
	},[]);


	const handleOnKeyDown = useCallback((event: KeyboardEvent<HTMLParagraphElement>) => {
		if (event.key === 'Escape') {
			setTempValue(value);
			setActiveEdit(false);
			return;
		}
		if (event.key === 'Enter') {
			handleCheckKey();
			return;
		}
	},[ value, tempValue ]);


	const handleCheckKey = useCallback(() => {
		if (editableRef && editableRef.current && exist(editableRef.current.innerText) && value !== tempValue) {
			setValue(tempValue);
			if (onPressSave !== undefined && onPressSave !== null) {
				onPressSave(tempValue);
			}
		}
		setActiveEdit(false);
	},[ editableRef, value, tempValue ]);


	const handleEditKey = useCallback(() => {
		if (value && value.length > 0) {
			setTempValue(value);
		}
		setActiveEdit(true);
	},[ value, tempValue ]);

	
	return (
		<div className={className ? `${className} editable-cell` : 'editable-cell'}>
			<p contentEditable={activeEdit}
				onKeyDown={handleOnKeyDown}
				onInput={onChangeInput}
				ref={editableRef}
				suppressContentEditableWarning={true}>
				{tempValue}
			</p>
			{!isApiError && !activeEdit && permissionToEdit &&
				<FontAwesomeIcon className='edit-icon' icon={faEdit} onClick={handleEditKey} />}
			{!isApiError && activeEdit &&
				<FontAwesomeIcon className='check-icon' icon={faCheck} onClick={handleCheckKey} />}
			{isApiError &&
				<FontAwesomeIcon className='danger-icon' icon={faCircleExclamation} />}
		</div>
	);
}

export default memo(EditableGridCell);